<template>
  <div class="gtm-demo-container">
    <b-alert v-if="currentView === 'settings' && showBanner" variant="primary" show class="mb-4" dismissible @dismissed="showBanner = false">
      <h5 class="alert-heading">GTM Integration Demo</h5>
      <p class="mb-0">
        This demo tests the iAdvize-GTM integration: configuring GTM, tracking page views and other events, and publishing ecommerce events (purchase) to internal kafka.
      </p>
      <hr class="my-2">
      <p class="mb-0">
        <strong>Future work:</strong> Adding Product Listing (PLP), Product Detail (PDP), shopping cart, 
        and checkout pages to test engagement starters and other product-related features.
      </p>
    </b-alert>

    <div v-if="currentView === 'settings'" class="settings-view">
      <SettingsGTM ref="gtmSettings" />
    </div>

    <div v-else-if="currentView === 'plp'" class="plp-view">
      <ProductListingPage @product-selected="selectProduct" />
    </div>

    <div v-else-if="currentView === 'pdp'" class="pdp-view">
      <ProductDetailPage 
        :selectedProduct="selectedProduct" 
        :cartItems="cartItems"
        @back-to-list="backToList"
        @add-to-cart="addToCart"
      />
    </div>

    <div v-else-if="currentView === 'cart'" class="cart-view">
      <ShoppingCart 
        :cartItems="cartItems"
        @continue-shopping="continueShopping"
        @checkout="checkoutCart"
        @update-cart="updateCart"
      />
    </div>

    <div class="navigation-buttons mt-4">
      <b-button-group>
        <b-button
          :variant="currentView === 'settings' ? 'primary' : 'outline-primary'"
          @click="currentView = 'settings'"
        >
          <b-icon-gear></b-icon-gear> GTM Settings
        </b-button>
        <b-button
          :variant="currentView === 'plp' ? 'primary' : 'outline-primary'"
          @click="currentView = 'plp'"
          :disabled="!gtmInitialized"
        >
          <b-icon-shop></b-icon-shop> Product Listing
        </b-button>
        <b-button
          :variant="currentView === 'pdp' ? 'primary' : 'outline-primary'"
          @click="currentView = 'pdp'"
          :disabled="!selectedProduct"
        >
          <b-icon-zoom-in></b-icon-zoom-in> Product Detail
        </b-button>
        <b-button
          :variant="currentView === 'cart' ? 'primary' : 'outline-primary'"
          @click="currentView = 'cart'"
          :disabled="cartItems.length === 0"
        >
          <b-icon-bag-check></b-icon-bag-check> Cart
          <b-badge v-if="cartItems.length" variant="light">{{ cartItems.length }}</b-badge>
        </b-button>
      </b-button-group>
    </div>

    <b-alert v-if="!gtmInitialized && currentView !== 'settings'" variant="info" show class="mt-3">
      Please initialize GTM in the Settings tab first.
    </b-alert>
  </div>
</template>

<script>
import SettingsGTM from './SettingsGTM.vue';
import ProductListingPage from './ProductListingPage.vue';
import ProductDetailPage from './ProductDetailPage.vue';
import ShoppingCart from './ShoppingCart.vue';

export default {
  name: 'GTMDemoContainer',
  components: {
    SettingsGTM,
    ProductListingPage,
    ProductDetailPage,
    ShoppingCart
  },
  data() {
    return {
      currentView: 'settings',
      gtmInitialized: false,
      selectedProduct: null,
      cartItems: [],
      showBanner: true,
      /** Map of product id -> overridden GTM product id (string) */
      gtmProductIdOverrides: {}
    };
  },
  provide() {
    const self = this;
    return {
      setGtmProductIdOverride(productId, overrideId) {
        self.setGtmProductIdOverride(productId, overrideId);
      },
      getGtmProductId(product) {
        const id = product && (product.id !== undefined) ? product.id : product;
        const over = self.gtmProductIdOverrides[id];
        return over != null && over !== '' ? String(over) : String(id);
      }
    };
  },
  methods: {
    setGtmProductIdOverride(productId, overrideId) {
      if (overrideId == null || String(overrideId).trim() === '') {
        this.$delete(this.gtmProductIdOverrides, productId);
        console.log('[GTM Demo] Product ID override cleared for product', productId);
        return;
      }
      const value = String(overrideId).trim();
      this.$set(this.gtmProductIdOverrides, productId, value);
      console.log('[GTM Demo] Product ID override set:', { productId, gtmProductId: value });
    },
    selectProduct(product) {
      this.selectedProduct = product;
      this.currentView = 'pdp';
    },
    backToList() {
      this.selectedProduct = null;
      this.currentView = 'plp';
    },
    addToCart(product, quantity) {
      console.log('addToCart called:', product.name, quantity);
      const existingItem = this.cartItems.find(item => item.id === product.id);
      
      if (existingItem) {
        existingItem.quantity = Math.min(existingItem.quantity + quantity, existingItem.stock);
      } else {
        this.cartItems.push({
          ...product,
          quantity: quantity
        });
      }
      console.log('Cart after add:', this.cartItems.length, this.cartItems);
    },
    continueShopping() {
      this.currentView = 'plp';
    },
    updateCart(items) {
      this.cartItems = items;
    },
    checkoutCart() {
      // Cart component handles checkout
    },
    handleGTMInitialized() {
      this.gtmInitialized = true;
    }
  },
  mounted() {
    // Listen for GTM initialization event
    this.$root.$on('gtm-initialized', this.handleGTMInitialized);
    // Check if GTM is already initialized
    this.$nextTick(() => {
      if (this.$refs.gtmSettings && this.$refs.gtmSettings.gtmInitialized) {
        this.gtmInitialized = true;
      }
    });
  },
  beforeDestroy() {
    this.$root.$off('gtm-initialized', this.handleGTMInitialized);
  },
  watch: {
    currentView(newVal) {
      // Check if GTM is initialized when navigating
      if ((newVal === 'plp' || newVal === 'pdp') && this.$refs.gtmSettings) {
        this.gtmInitialized = this.$refs.gtmSettings.gtmInitialized;
      }
    }
  }
};
</script>

<style scoped>
.gtm-demo-container {
  padding: 20px;
}

.settings-view,
.plp-view,
.pdp-view {
  min-height: 400px;
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.navigation-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}
</style>
